import { useGoogleLogin } from '@react-oauth/google';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { UnifiedIcon } from '@/components/UnifiedIcon';

export interface GoogleSsoButtonProps {
  disabled?: boolean;
  onError?: (message: string) => void;
  onClearNotice?: () => void;
}

/**
 * Google OAuth sign-in button (requires GoogleOAuthProvider in main.tsx).
 */
export function GoogleSsoButton({
  disabled = false,
  onError,
  onClearNotice,
}: GoogleSsoButtonProps) {
  const navigate = useNavigate();
  const { loginWithGoogleAccessToken, isLoading } = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleGoogleSuccess = useCallback(
    async (accessToken: string) => {
      onClearNotice?.();
      setIsGoogleLoading(true);

      try {
        const result = await loginWithGoogleAccessToken(accessToken);

        if (result.success) {
          navigate('/home', { replace: true });
          return;
        }

        if ('notOnboarded' in result && result.notOnboarded) {
          onError?.(result.message);
          return;
        }

        if ('error' in result && result.error) {
          onError?.(result.error.message);
        }
      } finally {
        setIsGoogleLoading(false);
      }
    },
    [loginWithGoogleAccessToken, navigate, onClearNotice, onError]
  );

  const startGoogleLogin = useGoogleLogin({
    onSuccess: (response) => {
      if (response.access_token) {
        void handleGoogleSuccess(response.access_token);
      } else {
        onError?.('Google sign-in did not return an access token.');
        setIsGoogleLoading(false);
      }
    },
    onError: () => {
      onError?.('Google sign-in was cancelled or failed.');
      setIsGoogleLoading(false);
    },
    onNonOAuthError: () => {
      onError?.('Google sign-in is unavailable in this browser.');
      setIsGoogleLoading(false);
    },
  });

  const handleClick = useCallback(() => {
    onClearNotice?.();
    setIsGoogleLoading(true);
    startGoogleLogin();
  }, [onClearNotice, startGoogleLogin]);

  const isBusy = disabled || isLoading || isGoogleLoading;

  return (
    <button
      type="button"
      data-testid="agents-login-google"
      disabled={isBusy}
      onClick={handleClick}
      className="ds-button-secondary w-full py-2.5 text-sm"
    >
      {isBusy ? (
        <>
          <UnifiedIcon name="CircleNotch" className="h-5 w-5 animate-spin" />
          Signing in with Google…
        </>
      ) : (
        <>
          <UnifiedIcon name="GoogleLogo" weight="bold" className="h-5 w-5" />
          Continue with Google
        </>
      )}
    </button>
  );
}
